// src/components/DUIInsuranceChatView.tsx
import React, { useEffect } from "react";
import { DUIInsuranceInfo } from "./DUI_Benefits_v01_DATA";

export const DUIInsurance_Benefits_v01: React.FC = () => {
  const data = DUIInsuranceInfo;

  // Inject lightweight chat-like styles once
  useEffect(() => {
    const id = "chat-like-styles";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
      .chatui {
        display: flex;
        justify-content: center;
        padding: 24px 12px;
        background: var(--bg, #f6f7f9);
      }
      .chatui .wrap {
        width: 100%;
        max-width: 760px;
      }

      /* Message rows */
      .msg {
        display: grid;
        grid-template-columns: 40px 1fr;
        gap: 12px;
        margin-bottom: 16px;
      }
      .avatar {
        width: 40px; height: 40px; border-radius: 50%;
        display: grid; place-items: center;
        font-weight: 700; font-size: 14px;
      }
      .avatar.user {
        background: #dbeafe; color: #1e3a8a; /* blue-100/900 */
      }
      .avatar.assistant {
        background: #e5e7eb; color: #111827; /* gray-200/900 */
      }

      .bubble {
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        padding: 14px 16px;
        box-shadow: 0 1px 1px rgba(0,0,0,.03);
      }
      .bubble.user {
        background: #eef5ff;
        border-color: #dbeafe;
      }

      /* Typography similar to ChatGPT */
      .bubble h1, .bubble h2, .bubble h3 {
        margin: 0 0 8px 0;
        color: #111827;
        font-weight: 700;
      }
      .bubble h2 { font-size: 18px; }
      .bubble h3 { font-size: 16px; }
      .bubble p, .bubble li, .bubble td, .bubble th {
        color: #111827;
        line-height: 1.6;
        font-size: 15px;
        margin: 0 0 8px 0;
      }
      .bubble ul, .bubble ol {
        padding-left: 1.25rem;
        margin: 6px 0 10px 0;
      }

      /* Simple table styling (ChatGPT-ish) */
      .bubble table {
        width: 100%;
        border-collapse: collapse;
        margin: 8px 0 4px 0;
        font-size: 14px;
      }
      .bubble thead th {
        text-align: left;
        border-bottom: 1px solid #e5e7eb;
        padding: 8px 6px;
      }
      .bubble tbody td {
        border-top: 1px solid #f0f0f0;
        padding: 8px 6px;
        vertical-align: top;
      }

      /* Section spacing */
      .section { margin-top: 10px; }
      .grid-3 {
        display: grid; gap: 10px;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      }
      .cardish {
        border: 1px solid #eee;
        border-radius: 10px;
        padding: 10px 12px;
        background: #fff;
      }

      /* Dark-mode friendly (optional) */
      @media (prefers-color-scheme: dark) {
        .chatui { --bg: #0b0f14; }
        .bubble, .cardish { background: #0f1720; border-color: #1f2937; }
        .bubble p, .bubble li, .bubble td, .bubble th, .bubble h1, .bubble h2, .bubble h3 { color: #e5e7eb; }
        .bubble thead th, .bubble tbody td { border-color: #1f2937; }
        .avatar.assistant { background: #1f2937; color: #e5e7eb; }
        .avatar.user { background: #0b2a4a; color: #cde1ff; }
        .bubble.user { background: #0c2440; border-color: #113358; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <div className="chatui">
      <div className="wrap">
        {/* User prompt */}
        <div className="msg">
          <div className="avatar user">U</div>
          <div className="bubble user">
            <p><strong>dui insurance - Features and Benefits</strong></p>
          </div>
        </div>

        {/* Assistant answer composed from your data */}
        <div className="msg">
          <div className="avatar assistant">A</div>
          <div className="bubble">
            <h2>{data.title}</h2>
            <p>{data.description}</p>

            <div className="section">
              <h3>Key Components</h3>
              <div className="grid-3">
                {data.keyComponents.map((k, i) => (
                  <div className="cardish" key={i}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{k.name}</h4>
                    <p style={{ marginTop: 6 }}>{k.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="section">
              <h3>Impact on Insurance</h3>
              <div className="cardish" style={{ marginBottom: 10 }}>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Premium Increases</h4>
                <p style={{ marginTop: 6 }}>{data.impacts.premiumIncreases.description}</p>
                <ul style={{ marginTop: 6 }}>
                  {data.impacts.premiumIncreases.examples.map((ex, i) => (
                    <li key={`pi-${i}`}><strong>{ex.state}:</strong> {ex.increase}</li>
                  ))}
                </ul>
              </div>

              <div className="cardish">
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Duration on Record</h4>
                <p style={{ marginTop: 6 }}>{data.impacts.duration.description}</p>
                <ul style={{ marginTop: 6 }}>
                  {data.impacts.duration.examples.map((ex, i) => (
                    <li key={`du-${i}`}><strong>{ex.state}:</strong> {ex.recordDuration}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="section">
              <h3>Benefits</h3>
              <ul>
                {data.benefits.map((b, i) => (
                  <li key={i}><strong>{b.title}:</strong> {b.description}</li>
                ))}
              </ul>
            </div>

            <div className="section">
              <h3>Quick Summary</h3>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 240 }}>Feature</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {data.summaryTable.map((row, i) => (
                    <tr key={i}>
                      <td>{row.feature}</td>
                      <td>{row.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Small meta line at bottom */}
            <p style={{ marginTop: 12, fontSize: 12, color: "#6b7280" }}>
              ID: {data.id}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DUIInsurance_Benefits_v01;
